import * as React from 'react';

import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion';

interface AccessibleVideoProps {
  src: string;
  ariaLabel: string;
  autoPlay?: boolean;
  loop?: boolean;
  playsInline?: boolean;
  preload?: 'none' | 'metadata' | 'auto';
  className?: string;
}

/**
 * AccessibleVideo - A WCAG 2.2.2 compliant auto-playing video component
 *
 * Features:
 * - Respects prefers-reduced-motion: won't autoplay if user prefers reduced motion
 * - Includes visible pause/play controls
 * - Allows users to pause looping content
 */
const AccessibleVideo: React.FC<AccessibleVideoProps> = ({
  src,
  ariaLabel,
  autoPlay = true,
  loop = true,
  playsInline = true,
  preload = 'metadata',
  className = '',
}) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  // Pause the video whenever the user enables reduced motion (including via a
  // mid-session OS preference change).
  React.useEffect(() => {
    if (prefersReducedMotion && videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  }, [prefersReducedMotion]);

  React.useEffect(() => {
    // Only autoplay if user doesn't prefer reduced motion
    if (autoPlay && !prefersReducedMotion && videoRef.current) {
      // play() returns a Promise in modern browsers, but may return undefined in test environments
      const playPromise = videoRef.current.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.then(() => {
          setIsPlaying(true);
        }).catch(() => {
          // Autoplay was prevented
          setIsPlaying(false);
        });
      }
    }
  }, [autoPlay, prefersReducedMotion]);

  const togglePlayPause = () => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      // play() returns a Promise in modern browsers, but may return undefined in test environments
      const playPromise = videoRef.current.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch(() => {
          // Play was prevented
          setIsPlaying(false);
        });
      }
      setIsPlaying(true);
    }
  };

  const handleVideoPlay = () => setIsPlaying(true);
  const handleVideoPause = () => setIsPlaying(false);

  return (
    <div className={`accessible-video-container ${className}`}>
      <video
        aria-label={ariaLabel}
        loop={loop}
        muted={true}
        onPause={handleVideoPause}
        onPlay={handleVideoPlay}
        playsInline={playsInline}
        preload={preload}
        ref={videoRef}
        src={src}
      />
      <button
        aria-label={isPlaying ? 'Pause video' : 'Play video'}
        className="accessible-video-toggle"
        onClick={togglePlayPause}
        type="button"
      >
        {isPlaying ? (
          <svg aria-hidden="true" fill="currentColor" height="24" viewBox="0 0 24 24" width="24">
            <rect height="14" width="4" x="6" y="5" />
            <rect height="14" width="4" x="14" y="5" />
          </svg>
        ) : (
          <svg aria-hidden="true" fill="currentColor" height="24" viewBox="0 0 24 24" width="24">
            <polygon points="5,3 19,12 5,21" />
          </svg>
        )}
      </button>
    </div>
  );
};

export default AccessibleVideo;
